import React, { useRef, useState } from 'react';
import { View, Text, Image, Pressable, Modal, useWindowDimensions } from 'react-native';
import { useNavigate } from 'react-router-dom';
import theme from '../../theme';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const routes = {
  Pricing: '/agents_pricing',
  Docs: '/agents_docs',
};

// Add each shipped product here — surfaces automatically in the Products dropdown.
const PRODUCTS = [
  { label: 'Agents', path: '/agents' },
  { label: 'Attribution', path: '/attribution' },
  { label: 'GA4', path: '/ga4' },
  { label: 'BI', path: '/bi' },
];

const Nav = () => {
  const { width } = useWindowDimensions();
  const isCompact = width < 480;
  const navigate = useNavigate();
  const [productsOpen, setProductsOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const triggerRef = useRef(null);

  const linkFontSize = isCompact ? theme.typography.size['2xs'] : theme.typography.size.sm;

  function openProducts() {
    if (triggerRef.current) {
      triggerRef.current.measureInWindow((x, y, w, h) => {
        setAnchor({ x, y, w, h });
        setProductsOpen(true);
      });
    } else {
      setProductsOpen(true);
    }
  }

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 18,
        paddingHorizontal: isCompact ? 20 : 48,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderDefault,
      }}
    >
      <Pressable onPress={() => navigate('/')} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 }}>
        <Image source={require('../../Images/stack_logo_red_png.png')} style={{ width: 20, height: 20, resizeMode: 'contain' }} />
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          style={{
            fontFamily: theme.typography.fontDisplay,
            fontSize: isCompact ? theme.typography.size.xs : theme.typography.size.md,
            letterSpacing: theme.trackingPx(theme.typography.size.md, 'tight'),
            color: theme.colors.textPrimary,
            flexShrink: 1,
          }}
        >
          Stack Technology
        </Text>
      </Pressable>

      <View style={{ flexDirection: 'row', alignItems: 'center', gap: isCompact ? 14 : 32, flexShrink: 0 }}>
        <Pressable ref={triggerRef} onPress={openProducts} style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
          <Text
            style={{
              fontFamily: theme.typography.fontBody,
              fontSize: linkFontSize,
              color: productsOpen ? theme.colors.textPrimary : theme.colors.textSecondary,
            }}
          >
            Products
          </Text>
          {productsOpen ? (
            <KeyboardArrowUpIcon style={{ fontSize: 16, color: theme.colors.textSecondary }} />
          ) : (
            <KeyboardArrowDownIcon style={{ fontSize: 16, color: theme.colors.textSecondary }} />
          )}
        </Pressable>

        {Object.entries(routes).map(([label, path]) => (
          <Pressable key={label} onPress={() => navigate(path)}>
            <Text
              style={{
                fontFamily: theme.typography.fontBody,
                fontSize: linkFontSize,
                color: theme.colors.textSecondary,
              }}
            >
              {label}
            </Text>
          </Pressable>
        ))}
      </View>

      {/* Rendered via Modal so it's on its own top-level layer — can't be occluded by sibling
          paint order (which is what was blocking taps when this was an absolutely-positioned View). */}
      <Modal visible={productsOpen} transparent animationType="fade" onRequestClose={() => setProductsOpen(false)}>
        <Pressable style={{ flex: 1 }} onPress={() => setProductsOpen(false)}>
          {anchor && (
            <View
              style={{
                position: 'absolute',
                top: anchor.y + anchor.h + theme.spacing.space2,
                left: anchor.x,
                minWidth: 220,
                backgroundColor: theme.colors.surfaceCard,
                borderWidth: theme.borderWidth.thin,
                borderColor: theme.colors.borderDefault,
                borderRadius: theme.radii.md,
                paddingVertical: theme.spacing.space2,
                ...theme.shadows.md,
              }}
            >
              {PRODUCTS.map((p) => (
                <Pressable
                  key={p.path}
                  onPress={() => {
                    setProductsOpen(false);
                    navigate(p.path);
                  }}
                  style={{ paddingVertical: theme.spacing.space2, paddingHorizontal: theme.spacing.space4 }}
                >
                  <Text style={{ fontFamily: theme.typography.fontBody, fontSize: theme.typography.size.sm, color: theme.colors.textPrimary }}>
                    {p.label}
                  </Text>
                </Pressable>
              ))}
            </View>
          )}
        </Pressable>
      </Modal>
    </View>
  );
};

export default Nav;
